<template>
  <div>
    <v-server-table
      id="reportTemplatesTable"
      ref="reportTemplatesTable"
      :name="'reportTemplatesTable'"
      :columns="columns"
      :options="tableOptions"
    >
      <span slot="updated_at" slot-scope="props">
              {{ props.row.updated_at | localTime('YYYY-MM-DD HH:mm') }}
      </span>

      <span slot="entities" slot-scope="props">
              {{ props.row.entity_cluster
        ? presentCluster(props.row.entity_cluster)
        : presentEntities(props.row.entities) || $t('reports.templates.labels.no_facilities') }}
            </span>

      <span slot="facilities" slot-scope="props">
              {{ props.row.facility_cluster
        ? presentCluster(props.row.facility_cluster)
        : presentFacilities(props.row.facilities) || $t('reports.templates.labels.no_facilities') }}
            </span>

      <span slot="report_type" slot-scope="props">
              {{ $t(`reports.templates.labels.report_types.${reportTypesAdapter[props.row.report_type]}`)}}
      </span>

      <span slot="client_type" slot-scope="props">
          {{ $t(`reports.templates.labels.client_types.${reportClientTypesAdapter[props.row.client_type]}`)}}
      </span>

      <span slot="is_control" slot-scope="props">
        <span>{{
            props.row.is_control ? $t('reports.reports.labels.is_control_true') : $t('reports.reports.labels.is_control_fase')
          }}</span>
      </span>

      <span slot="actions" slot-scope="props" class="d-inline-block w-100 text-center">
        <va-popover
        v-if="canGenerateReport(props.row)"
        class="mr-1"
        placement="left"
        :message="$t('reports.templates.buttons.generate_report')">
          <i class="report-action-icon fa fa fa-file-excel-o p-2" @click.prevent.stop="generateReport(props.row)"> </i>
        </va-popover>

        <va-popover
          v-if="canUpdateTemplate(props.row)"
          class="mr-1"
          placement="left"
          :message="$t('reports.templates.buttons.update_template')">
          <i class="report-action-icon fa fa-pencil p-2" @click.prevent.stop="updateTemplate(props.row)"> </i>
        </va-popover>

        <va-popover
          v-if="canDeleteTemplate(props.row)"
          placement="left"
          :message="$t('reports.templates.buttons.delete_template')">
          <i class="report-action-icon fa fa-times-circle p-2" @click.prevent.stop="deleteTemplate(props.row)"> </i>
        </va-popover>
      </span>

    </v-server-table>
  </div>
</template>

<script>
import TableQueryMixin from '@/mixins/TableQueryMixin'
import MultiselectLabelFormatter from '@/mixins/MultiselectLabelFormatter'
import ClustersAndEntitiesMixin from '@/mixins/ClustersAndEntitiesMixin'

const templateInfoType = 200
const reportsInfoType = 210

const readAccessType = 1
const writeAccessType = 2

const entityInfoType = 110

export default {
  props: {
    querySearch: {
      type: String,
      required: false,
      default: null
    },
    serviceUrl: {
      type: String,
      required: true
    },
    clusters: {
      type: Array,
      required: true
    },
    facilities: {
      type: Array,
      required: true
    },
    reportTypesAdapter: {
      type: Object,
      required: true
    }
  },
  mixins: [
    TableQueryMixin,
    MultiselectLabelFormatter,
    ClustersAndEntitiesMixin
  ],
  data: function () {
    return {
      selectedReportTemplateForUpdate: undefined,
      selectedReportTemplateForGeneration: undefined,
      selectedReportTemplateForDeletion: undefined,
      columns: ['updated_at', 'name', 'entities', 'facilities', 'report_type', 'client_type', 'is_control', 'actions'],
      queryUrl: `${this.$store.state.reports_server}/templates`,
      filters: {
        querySearch: '',
      },
      reportClientTypesAdapter: {
        1: 'client',
        2: 'provider'
      },
      tableOptions: {
        uniqueKey: 'id',
        saveState: true,
        filterable: true,
        customFilters: [],
        sortable: ['updated_at', 'name'],
        headings: {
          updated_at: this.$t('reports.templates.table.updated_at'),
          name: this.$t('reports.templates.table.name'),
          entities: this.$t('reports.templates.table.clients'),
          facilities: this.$t('reports.templates.table.facilities'),
          report_type: this.$t('reports.templates.table.report_type'),
          client_type: this.$t('reports.templates.table.client_type'),
          actions: this.$t('reports.templates.table.actions'),
          is_control: this.$t('reports.templates.table.is_control')
        },
        columnsClasses: {
          updated_at: 'updated_at',
          name: 'name',
          entities: 'entities',
          facilities: 'facilities',
          report_type: 'report_type',
          client_type: 'client_type',
          actions: 'text-center',
        }
      },
    }
  },

  methods: {
    presentCluster: function (cluster) {
      return `${cluster.name} 📂 (${cluster.aggregatedData.length} ${this.$t('reports.templates.labels.entities')})`
    },
    presentEntities: function (entities) {
      if (entities !== null && entities.length) {
        let result = entities.slice(0, 3).map(element => element.cities.length === 0 ? element.name : `${element.name} (${element.cities.join(', ')})`).join('; ')
        if (entities.length > 3) {
          result = `${result} (${this.$t('tables.multiselect_limitText_and')} ${entities.length - 3} ${this.$t('tables.multiselect_limitText_others')})`
        }
        return result
      }
    },
    presentFacilities: function (value) {
      if (value !== null && value.length) {
        let result = value.slice(0, 3).map(element => element.address && element.address.city ? `${element.name} (${element.address.city})` : element.name).join('; ')
        if (value.length > 3) {
          result = `${result} (${this.$t('tables.multiselect_limitText_and')} ${value.length - 3} ${this.$t('tables.multiselect_limitText_others')})`
        }
        return result
      }
    },
    canGenerateReport: function (template) {
      let facilities = []
      if (template.facilities.length !== 0 && template.facility_cluster !== null) {
        facilities = template.facility_cluster ? template.facility_cluster.aggregatedData : template.facilities
      }

      let entities = template.entity_cluster ? template.entity_cluster.aggregatedData : template.entities

      if (facilities) {
        return entities.some(entity => this.writableReportEntities.some(writableEntity => writableEntity.id === entity.id)) &&
          facilities.every(facility => this.readableAuthEntities.some(entity => entity.id === facility.entity_id))
      }
      return entities.some(entity => this.writableReportEntities.some(crossedEntity => crossedEntity.id === entity.id))
    },
    canUpdateTemplate: function (template) {
      // Must have permissions for all clients (auth read entities + reports write templates)
      let hasClientPermissions = template.entity_cluster ? this.clusters.some(cluster => cluster.id === template.entity_cluster.id) : template.entities.every(entity => this.crossedWritableTemplateEntities.some(writableEntity => entity.id === writableEntity.id))

      if (hasClientPermissions) {
        // Must have permissions for all facilities (auth read entities)
        return template.facility_cluster ? this.clusters.some(cluster => cluster.id === template.facility_cluster.id) : template.facilities.every(facility => this.readableAuthEntities.some(entity => facility.entity_id === entity.id))
      }
      return false
    },
    canDeleteTemplate: function (template) {
      // Must have permissions for all clients (reports write templates)
      if (template.entity_cluster) {
        return this.clusters.some(cluster => cluster.id === template.entity_cluster.id)
      } else {
        return template.entities.every(entity => this.writableTemplateEntities.some(writableEntity => entity.id === writableEntity.id))
      }
    },
    filterPermissions: function (permissions, accessType, infoType) {
      return permissions.filter(permission => permission.access_type >= accessType && permission.info_type === infoType)
    },
    generateReport: function (report) {
      this.selectedReportTemplateForGeneration = report
      this.$emit('generate-report', { report: this.selectedReportTemplateForGeneration, writableEntities: this.writableReportEntities })
    },
    updateTemplate: function (template) {
      this.selectedReportTemplateForUpdate = template
      this.$emit('update-template', { template: this.selectedReportTemplateForUpdate, crossedWritableTemplateEntities: this.crossedWritableTemplateEntities })
    },
    deleteTemplate: function (template) {
      this.selectedReportTemplateForDeletion = template
      this.$emit('delete-template', this.selectedReportTemplateForDeletion)
    },
  },
  computed: {
    reportsService: function () {
      return this.$store.state.services.find(element => element.url === this.serviceUrl)
    },
    authService: function () {
      return this.$store.state.services.find(element => element.url === this.$store.state.evollauth_server)
    },
    writableTemplateEntities: function () {
      if (this.reportsService) {
        let writeTemplatePermissions = this.filterPermissions(this.reportsService.user_permissions, writeAccessType, templateInfoType)

        if (writeTemplatePermissions.length > 0) {
          if (writeTemplatePermissions.some(permission => permission.entity_id === null)) {
            return [...this.reportsService.entities]
          } else {
            return writeTemplatePermissions.map(permission => {
              return this.reportsService.entities.find(entity => entity.id === permission.entity_id)
            })
          }
        }
      }
      return []
    },
    crossedWritableTemplateEntities: function () {
      if (this.reportsService && this.readableAuthEntities.length > 0 && this.writableTemplateEntities.length > 0) {
        return this.readableAuthEntities.filter(authEntity => this.writableTemplateEntities.some(reportsEntity => reportsEntity.id === authEntity.id))
      } else {
        return []
      }
    },
    readableAuthEntities: function () {
      if (this.authService) {
        let readableEntityPermissions = this.filterPermissions(this.authService.user_permissions, readAccessType, entityInfoType)
        if (readableEntityPermissions.length > 0) {
          let readableEntities = []
          if (readableEntityPermissions[0].entity_id === null) {
            readableEntities = this.authService.entities
          } else {
            readableEntityPermissions.forEach(permission => {
              readableEntities.push(this.authService.entities.find(entity => entity.id === permission.entity_id))
            })
          }
          return readableEntities
        }
      }
      return []
    },
    writableReportEntities: function () {
      if (this.reportsService) {
        let writableReportPermissions = this.filterPermissions(this.reportsService.user_permissions, writeAccessType, reportsInfoType)

        if (writableReportPermissions.length > 0) {
          let writableEntities = []

          if (writableReportPermissions[0].entity_id === null) {
            writableEntities = this.reportsService.entities
          } else {
            writableReportPermissions.forEach(permission => {
              writableEntities.push(this.reportsService.entities.find(entity => entity.id === permission.entity_id))
            })
          }
          return writableEntities
        }
      }
      return []
    },
  },
  watch: {
    querySearch: {
      handler: function (value) {
        this.filters.querySearch = value || ''
      },
      immediate: true
    },
    'filters.querySearch': function (value) {
      this.$refs.reportTemplatesTable.setFilter(value)
    },
  }
}
</script>

<style scoped lang="scss">
</style>
